import { useMemo } from 'react';
import { DefaultUsageTracker, MockUsageTracker } from 'crm-cards/internal/UsageTracker';
import CrmCardContainer from './CrmCardContainer';
import { isSidebarLocation } from 'crm-cards-sdk/constants/CrmLocationName';
import CrmCardFoundationProvider from './CrmCardFoundationProvider';
import { useHasLegacySidebarCard } from './internal/sidebarCards/hooks/useHasLegacySidebarCard';
import Raven from 'raven-js';
import { createIntersectionObserverManager } from 'crm-cards-sdk/internal/IntersectionObserverManager';
import { InitialCardsReadyRhumb } from './internal/InitialCardsReadyRhumb';
import { CrmLocationReady, getCrmLocationReady } from 'crm-fe-perf/timing/marks/crm-cards/performanceMarks';
import PerformanceMarker from 'crm-fe-perf/timing/PerformanceMarker';
import useMarkOnMount from 'crm-fe-perf/timing/hooks/useMarkOnMount';
import { getCrmCardLocationStart } from 'crm-fe-perf/timing/marks/crm-cards-sdk/performanceMarks';
import { NavMarker } from 'react-rhumb';
import { getIsFirstLoad, getWasHidden } from 'crm-cards-sdk/utils/sharedPerfDimensions';
import { DEFAULT_CONTAINER_FORMATTERS, mergeWithDefaultFormatters } from './container-formatters/ContainerFormatterRegistry';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const INTERSECTION_OBSERVER_OPTIONS = {
  root: null,
  // Use the top-level document's viewport
  rootMargin: '200px 0px',
  threshold: 0
};
export default function CrmCardLocation(props) {
  const {
    additionalLocationData,
    data,
    disableInternalRhumbMarkers = false,
    disableUsageTracker,
    dragDropComponents,
    editableAreasAtom,
    features = [],
    focusedItem,
    customLayouts,
    gates,
    httpClient,
    i18nLoader,
    legacySidebarCardProps,
    location,
    locationService,
    loggingTag,
    propertySource = 'CRM_UI',
    registry,
    scopes,
    topSlot,
    usageTracker = props.disableUsageTracker ? MockUsageTracker : DefaultUsageTracker,
    enableVirtualization = false,
    offscreenCardsPolicy
  } = props;
  const hasLegacySidebarCard = useHasLegacySidebarCard(data.container);
  const dataAttributes = useMemo(() => {
    const baseDataAttributes = {
      'data-crm-location': data.crmLocationName
    };
    if (isSidebarLocation(data.crmLocationName)) {
      return Object.assign({}, baseDataAttributes, {
        'data-sidebar-location': data.crmLocationName
      });
    }
    return baseDataAttributes;
  }, [data.crmLocationName]);

  // Merge the provided custom layouts with the default if any are provided
  const formatterRegistry = useMemo(() => {
    if (customLayouts) {
      return mergeWithDefaultFormatters(customLayouts);
    }
    return DEFAULT_CONTAINER_FORMATTERS;
  }, [customLayouts]);
  if (location === 'CUSTOM' && !loggingTag) {
    console.error('CrmCrmLocation: CUSTOM location requires a loggingTag to be passed in.');
    Raven.captureException(new Error('CrmCrmLocation: CUSTOM location requires a loggingTag to be passed in.'));
  }
  const observerManager = useMemo(() => createIntersectionObserverManager(INTERSECTION_OBSERVER_OPTIONS), []);
  const perfDimensions = useMemo(() => ({
    location,
    logging_tag: loggingTag || '',
    is_first_load: getIsFirstLoad(location, loggingTag),
    was_hidden: getWasHidden()
  }),
  // We don't track it, but we should re-measure whenever the object changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [location, loggingTag, additionalLocationData]);
  useMarkOnMount(getCrmCardLocationStart(location, loggingTag), perfDimensions);
  return /*#__PURE__*/_jsxs(CrmCardFoundationProvider, {
    additionalLocationData: additionalLocationData,
    disableUsageTracker: disableUsageTracker,
    dragDropComponents: dragDropComponents,
    editableAreasAtom: editableAreasAtom,
    features: features,
    gates: gates,
    httpClient: httpClient,
    i18nLoader: i18nLoader,
    legacySidebarCardProps: legacySidebarCardProps,
    location: location,
    locationService: locationService,
    loggingTag: loggingTag || 'CrmCardLocation Default Tag' // Needed to default a Tag because the types don't like passing through the LocationDependantProps
    ,
    propertySource: propertySource,
    scopes: scopes,
    supportLegacyCards: hasLegacySidebarCard,
    usageTracker: usageTracker,
    enableVirtualization: enableVirtualization,
    offscreenCardsPolicy: offscreenCardsPolicy,
    viewId: data.viewId,
    children: [/*#__PURE__*/_jsx(CrmCardContainer, {
      containerId: data.container.id,
      content: data.container,
      dataAttributes: dataAttributes,
      isScrollable: true,
      topSlot: topSlot,
      i18nLoader: i18nLoader,
      registry: registry,
      usageTracker: usageTracker,
      focusedItem: focusedItem,
      observerManager: observerManager,
      formatterRegistry: formatterRegistry
    }, `${data.container.id}-${location}-${loggingTag}-${additionalLocationData.objectId || ''}-${additionalLocationData.objectTypeId || ''}-${additionalLocationData.viewId}`), /*#__PURE__*/_jsx(PerformanceMarker, {
      marker: CrmLocationReady,
      metricsAdditionalDimensions: perfDimensions
    }), /*#__PURE__*/_jsx(PerformanceMarker, {
      marker: getCrmLocationReady(location),
      metricsAdditionalDimensions: perfDimensions
    }), !disableInternalRhumbMarkers && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(NavMarker, {
        name: `LOCATION_${location}_READY`
      }), /*#__PURE__*/_jsx(InitialCardsReadyRhumb, {
        location: location
      })]
    })]
  });
}